import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Box, Button, Heading, Text, Input, VStack } from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import LandingHeader from "../../components/LandingHeader/LandingHeader";
import LandingFooter from "../../components/LandingFooter/LandingFooter";
import SubPageHero from "../../components/SubPageHero/SubPageHero";
import Turnstile from "../../components/Turnstile";
import {
  STAGE_LABELS,
  BIO_PLY_LEAD,
  FDA_PATHWAY_FOOTNOTE,
  stageStatusLabel,
} from "./pipelineData";
import { captureLead, LEAD_SEGMENTS } from "../../utils/leadCapture";
import { notifyLead } from "../../utils/leadNotify";
import "./Landing.css";
import "./BioPly.css";
import Icon from '../../components/ui/Icon';
import Seo, { SITE_URL } from '../../components/Seo/Seo';

const bioplyJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'BIO-PLY™',
  description:
    'BIO-PLY™ is a first-in-class, triple-action platelet-derived peptide biologic developed specifically for equine septic joints — addressing infection, inflammation, and tissue damage in one ready-to-use treatment.',
  category: 'Veterinary peptide biologic',
  brand: { '@type': 'Brand', name: 'Qentoros' },
  manufacturer: { '@type': 'Organization', name: 'Qentoros' },
  image: `${SITE_URL}/images/products-hero.jpg`,
  url: `${SITE_URL}/products/bio-ply`,
};

const QUOTE_INTERVAL_MS = 6000;

// Veterinarian testimonials — add entries here and the carousel scales.
const TESTIMONIALS = [
  {
    quote:
      "BIO-PLY was instrumental in all 10 septic joint cases we treated. Horses improved faster, even those with chronic and challenging infections.",
    name: "Dr. Molly Patton",
    affiliation: "Equine Specialty Hospital of Burton, OH",
    monogram: "MP",
  },
  {
    quote: "BIO-PLY has been a game changer for us.",
    name: "Dr. Steve Temming",
    affiliation: "Columbus, NJ",
    monogram: "ST",
  },
  {
    quote:
      "BIO-PLY has become an indispensable part of my septic arthritis treatment protocol, and after seeing the outcomes firsthand, I can’t imagine managing these cases without it.",
    name: "Dr. Nate Lea",
    affiliation: "Outlaw Equine",
    monogram: "NL",
  },
];

const MotionBox = motion(Box);
const publicUrl = (import.meta.env.BASE_URL || "").replace(/\/$/, "");
const heroImageUrl = `${publicUrl}/images/bioply-hero.jpg`;
const contactImageUrl = `${publicUrl}/images/bioply-contact.jpg`;
const patientImageUrl = `${publicUrl}/images/far-better-race.jpg`;
const peptideImageUrl = `${publicUrl}/images/peptide.jpg`;

export default function BioPly() {
  const [contactEmail, setContactEmail] = useState("");
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactError, setContactError] = useState("");
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [quoteIndex, setQuoteIndex] = useState(0);
  // Auto-advance pauses while the visitor is reading (hover or keyboard focus),
  // so a quote is never yanked away mid-sentence.
  const [quotesPaused, setQuotesPaused] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const turnstileRef = useRef(null);

  const activeQuote = TESTIMONIALS[quoteIndex];
  const goToQuote = (i) =>
    setQuoteIndex((i + TESTIMONIALS.length) % TESTIMONIALS.length);

  // Cycle the testimonials on a timer. Keyed off `quoteIndex`, so the countdown
  // restarts whenever the visitor taps a dot or an arrow rather than firing part
  // way through the quote they just chose. Skipped entirely under reduced motion.
  useEffect(() => {
    if (quotesPaused || TESTIMONIALS.length < 2) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches)
      return undefined;
    const id = setTimeout(
      () => setQuoteIndex((i) => (i + 1) % TESTIMONIALS.length),
      QUOTE_INTERVAL_MS,
    );
    return () => clearTimeout(id);
  }, [quoteIndex, quotesPaused]);

  // Pause on keyboard focus only. A mouse click also focuses the dot, and
  // pausing on that leaves the carousel stalled for good after a single tap.
  const handleQuoteFocusCapture = (e) => {
    if (e.target.matches?.(":focus-visible")) setQuotesPaused(true);
  };

  // "Request further information" is a lightweight interest capture — just the
  // visitor's email — not the full request-access wizard. Save it as a contact
  // lead (it surfaces on the admin leads list) and show a confirmation so the
  // team can follow up.
  //
  // The capture is awaited rather than fire-and-forget: a tokened submission has
  // to clear Turnstile server-side in submit-lead, and that call can fail (token
  // rejected, function down). Confirming a request whose row was never written
  // loses the lead with nobody aware of it.
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactEmail.trim() || contactSubmitting) return;

    setContactSubmitting(true);
    setContactError("");

    const { error: captureError } = await captureLead({
      email: contactEmail,
      segment: LEAD_SEGMENTS.CONTACT,
      source: "bioply",
      payload: { intent: "bio_ply_inquiry" },
      token: captchaToken,
    });

    // Token is single-use; clear it whatever the outcome so a retry
    // re-challenges.
    turnstileRef.current?.reset();
    setCaptchaToken("");
    setContactSubmitting(false);

    if (captureError) {
      setContactError(
        "Something went wrong sending your request. Please try again."
      );
      return;
    }

    // Auto-reply to the visitor and alert the team. Fire-and-forget: the lead
    // is already saved, so a mail hiccup must not block the confirmation.
    notifyLead({ email: contactEmail, segment: LEAD_SEGMENTS.CONTACT });

    setContactSubmitted(true);
  };

  return (
    <div className="landing-page">
      <Seo
        title="BIO-PLY™ | Triple-Action Peptide Biologic for Equine Septic Joints"
        description="BIO-PLY™ is a first-in-class, triple-action platelet-derived peptide biologic developed specifically for equine septic joints — addressing infection, inflammation, and tissue damage in one ready-to-use treatment."
        path="/products/bio-ply"
        jsonLd={bioplyJsonLd}
      />
      <LandingHeader />

      <main id="main" tabIndex={-1}>
        <div className="bioply-hero-wrap">
          <SubPageHero
            eyebrow="BIO-PLY™"
            title="A first-in-class, triple-action peptide biologic."
            subtitle="Developed specifically for equine septic joints, BIO-PLY™ addresses all three major challenges — infection, inflammation, and tissue damage — in a single ready-to-use treatment."
            badges={["Antimicrobial", "Anti-inflammatory", "Chondroprotective"]}
            backgroundImage={heroImageUrl}
            cta={{ label: "Request BIO-PLY™", href: "/order" }}
            secondaryCta={{ label: "Talk to our team", href: "#contact" }}
          />
        </div>

        {/* Problem — orthopedic infections are devastating to horses */}
        <section
          id="problem"
          className="landing-section bioply-problem"
          aria-labelledby="problem-heading"
        >
          <div className="bioply-problem-head">
            <p className="bioply-overline">The problem</p>
            <Heading
              as="h2"
              id="problem-heading"
              className="bioply-display"
              color="gray.900"
              mb={4}
            >
              Septic arthritis is devastating to horses.
            </Heading>
            <Text color="gray.600" fontSize="lg" maxW="66ch">
              When a joint, tendon sheath, or bursa becomes infected, the current
              standard of care often can't keep up, and the damage compounds fast.
            </Text>
          </div>

          <div className="bioply-flip">
            <div className="bioply-flip-col bioply-flip-col--problem">
              <p className="bioply-flip-col-label">Current standard of care</p>
              <ul className="bioply-flip-list">
                <li>
                  <span className="bioply-flip-icon" aria-hidden="true">
                    <Icon name="shield" />
                  </span>
                  <div>
                    <h3 className="bioply-flip-item-title">Infection that won't clear</h3>
                    <p className="bioply-flip-item-sub">
                      Bacteria hide in antibiotic-resistant biofilms, so standard
                      antibiotics can't reach or clear the infection.
                    </p>
                  </div>
                </li>
                <li>
                  <span className="bioply-flip-icon" aria-hidden="true">
                    <Icon name="healing" />
                  </span>
                  <div>
                    <h3 className="bioply-flip-item-title">Cartilage destroyed</h3>
                    <p className="bioply-flip-item-sub">
                      Runaway inflammation eats away at cartilage and soft tissue,
                      often irreversibly.
                    </p>
                  </div>
                </li>
                <li>
                  <span className="bioply-flip-icon" aria-hidden="true">
                    <Icon name="heart_broken" />
                  </span>
                  <div>
                    <h3 className="bioply-flip-item-title">Careers and lives lost</h3>
                    <p className="bioply-flip-item-sub">
                      Many affected horses never return to performance, and some
                      do not survive.
                    </p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bioply-flip-arrow" aria-hidden="true">
              <Icon name="arrow_forward" />
            </div>

            <MotionBox
              className="bioply-flip-col bioply-flip-col--solution"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-24px" }}
              transition={{ duration: 0.45 }}
            >
              <p className="bioply-flip-col-label">With BIO-PLY™</p>
              <h3 className="bioply-flip-solution-title">
                <span className="bioply-brand-accent">BIO-PLY™</span> succeeds
                where standard of care fails.
              </h3>
              <ul className="bioply-flip-list bioply-flip-list--solution">
                <li>
                  <span className="bioply-flip-icon" aria-hidden="true">
                    <Icon name="check_circle" />
                  </span>
                  <div>
                    <h4 className="bioply-flip-item-title">Goes after the infection</h4>
                    <p className="bioply-flip-item-sub">
                      Directly antibacterial, active even against the biofilms
                      that shield bacteria from antibiotics.
                    </p>
                  </div>
                </li>
                <li>
                  <span className="bioply-flip-icon" aria-hidden="true">
                    <Icon name="check_circle" />
                  </span>
                  <div>
                    <h4 className="bioply-flip-item-title">Calms joint inflammation</h4>
                    <p className="bioply-flip-item-sub">
                      Resolves runaway inflammation without shutting down a
                      productive immune response.
                    </p>
                  </div>
                </li>
                <li>
                  <span className="bioply-flip-icon" aria-hidden="true">
                    <Icon name="check_circle" />
                  </span>
                  <div>
                    <h4 className="bioply-flip-item-title">Protects cartilage</h4>
                    <p className="bioply-flip-item-sub">
                      Helps preserve cartilage and soft tissue from the
                      irreversible damage of joint sepsis.
                    </p>
                  </div>
                </li>
              </ul>
            </MotionBox>
          </div>
        </section>

        {/* What is BIO-PLY™ — editorial 2-col with image slot */}
        <section
          id="what-is"
          className="landing-section bioply-what"
          aria-labelledby="what-is-heading"
        >
          <div className="bioply-what-grid">
            <div className="bioply-what-copy">
              <p className="bioply-overline">The solution</p>
              <Heading
                as="h2"
                id="what-is-heading"
                className="bioply-display"
                color="gray.900"
                mb={4}
              >
                What is BIO-PLY™?
              </Heading>
              <Text
                color="gray.500"
                fontSize="lg"
                mb={6}
                className="bioply-what-deck"
              >
                A platelet-derived peptide biologic manufactured for
                consistency, quality, and clinical performance.
              </Text>
              <Text className="bioply-what-body" color="gray.700" mb={5}>
                BIO-PLY™ is a platelet-derived peptide biologic developed for
                the treatment of equine septic synovial infections. Laboratory
                and veterinary studies have demonstrated antibacterial,
                immune-modulating, and tissue-protective activity, supporting
                its ability to help control infection while preserving joint
                health.
              </Text>
              <Text className="bioply-what-body" color="gray.700" mb={5}>
                BIO-PLY™ is manufactured through a carefully controlled process
                focused on consistency, safety, and quality. Platelets are
                sourced from a closed, screened donor herd, and every production
                lot undergoes comprehensive quality and potency testing before
                release. BIO-PLY™ is currently advancing through FDA-authorized
                clinical development for equine septic synovial infections under
                the oversight of the FDA-CVM.
              </Text>
              <ul
                className="bioply-what-pills"
                aria-label="Key product attributes"
              >
                <li>
                  <Icon name="bloodtype" aria-hidden="true" /> Platelet-derived
                </li>
                <li>
                  <Icon name="dna" aria-hidden="true" /> Peptide biologic
                </li>
                <li>
                  <Icon name="factory" aria-hidden="true" /> cGMP manufactured
                </li>
                <li>
                  <Icon name="health_and_safety" aria-hidden="true" />{" "}
                  Health-screened donor herd
                </li>
                <li>
                  <Icon name="check_circle" aria-hidden="true" /> Lot release
                  tested
                </li>
              </ul>
            </div>
            <div className="bioply-what-visual" aria-hidden="true">
              <div className="bioply-image-slot bioply-image-slot--3x2 bioply-what-image">
                <img src={peptideImageUrl} alt="" loading="lazy" />
              </div>
            </div>
          </div>
        </section>

        {/* Clinical proof — FDA-supervised study (moved to end of page) */}
        <section
          id="proof"
          className="landing-section bioply-proof"
          aria-labelledby="proof-heading"
        >
          <header className="bioply-proof-header">
            <p className="bioply-overline">FDA-supervised pilot trial</p>
            <Heading
              as="h2"
              id="proof-heading"
              className="bioply-display"
              color="gray.900"
              mb={3}
            >
              Delivering great results for equine health.
            </Heading>
            <Text color="gray.600" fontSize="lg" maxW="66ch">
              Early findings from our multi-site clinical study are encouraging:
              BIO-PLY™ is showing improved outcomes versus the current standard
              of care for septic synovitis, without a single adverse event
              reported to date.
            </Text>
          </header>

          <ul className="bioply-proof-meta" aria-label="Study scope">
            <li>
              <strong>100+</strong>
              <span>cases</span>
            </li>
            <li>
              <strong>19</strong>
              <span>sites</span>
            </li>
            <li>
              <strong>28</strong>
              <span>investigators</span>
            </li>
            <li>
              <strong>0</strong>
              <span>adverse events</span>
            </li>
          </ul>

          <div className="bioply-proof-grid">
            <MotionBox
              as="article"
              className="bioply-proof-card"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-24px" }}
              transition={{ duration: 0.45 }}
            >
              <div className="bioply-proof-card-icon" aria-hidden="true">
                <Icon name="favorite" />
              </div>
              <div className="bioply-proof-card-eyebrow">Survival</div>
              <div className="bioply-proof-card-value">
                98<span>%</span>
              </div>
              <div className="bioply-proof-card-caption">
                survival to date across treated cases
              </div>
              <div
                className="bioply-proof-bars"
                aria-label="Survival comparison"
              >
                <div className="bioply-proof-bar">
                  <div className="bioply-proof-bar-meta">
                    <span className="bioply-proof-bar-name bioply-proof-bar-name-primary">
                      BIO-PLY™
                    </span>
                    <span className="bioply-proof-bar-pct bioply-proof-bar-pct-primary">
                      98%
                    </span>
                  </div>
                  <div className="bioply-proof-bar-track">
                    <div
                      className="bioply-proof-bar-fill bioply-proof-bar-fill-primary"
                      style={{ width: "98%" }}
                    />
                  </div>
                </div>
                <div className="bioply-proof-bar">
                  <div className="bioply-proof-bar-meta">
                    <span className="bioply-proof-bar-name">
                      Standard of care
                    </span>
                    <span className="bioply-proof-bar-pct">85%</span>
                  </div>
                  <div className="bioply-proof-bar-track">
                    <div
                      className="bioply-proof-bar-fill"
                      style={{ width: "85%" }}
                    />
                  </div>
                </div>
              </div>
            </MotionBox>

            <MotionBox
              as="article"
              className="bioply-proof-card"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-24px" }}
              transition={{ duration: 0.45, delay: 0.08 }}
            >
              <div className="bioply-proof-card-icon" aria-hidden="true">
                <Icon name="military_tech" />
              </div>
              <div className="bioply-proof-card-eyebrow">
                Return to athletic function
              </div>
              <div className="bioply-proof-card-value">
                85<span>%</span>
              </div>
              <div className="bioply-proof-card-caption">
                returned to athletic function
              </div>
              <div
                className="bioply-proof-bars"
                aria-label="Return-to-athletic-function comparison"
              >
                <div className="bioply-proof-bar">
                  <div className="bioply-proof-bar-meta">
                    <span className="bioply-proof-bar-name bioply-proof-bar-name-primary">
                      BIO-PLY™
                    </span>
                    <span className="bioply-proof-bar-pct bioply-proof-bar-pct-primary">
                      85%
                    </span>
                  </div>
                  <div className="bioply-proof-bar-track">
                    <div
                      className="bioply-proof-bar-fill bioply-proof-bar-fill-primary"
                      style={{ width: "85%" }}
                    />
                  </div>
                </div>
                <div className="bioply-proof-bar">
                  <div className="bioply-proof-bar-meta">
                    <span className="bioply-proof-bar-name">
                      Standard of care
                    </span>
                    <span className="bioply-proof-bar-pct">66%</span>
                  </div>
                  <div className="bioply-proof-bar-track">
                    <div
                      className="bioply-proof-bar-fill"
                      style={{ width: "66%" }}
                    />
                  </div>
                </div>
              </div>
            </MotionBox>
          </div>

          <p className="bioply-proof-footnote" role="note">
            <Icon name="science" />
            FDA-supervised, multi-site clinical study &middot; data collection is
            ongoing, so results are subject to change. Standard-of-care figures
            are historical, drawn from a meta-analysis of published literature.
          </p>
        </section>

        {/* Patient case study — Far Better, full-bleed dark feature */}
        <section
          id="patient-story"
          className="landing-section bioply-patient bioply-section--dark"
          aria-labelledby="patient-heading"
        >
          <header className="bioply-patient-header">
            <p className="bioply-patient-eyebrow">Patient story</p>
            <Heading
              as="h2"
              id="patient-heading"
              className="bioply-display"
              mb={3}
            >
              From career-ending injury to the winner's circle.
            </Heading>
            <Text color="rgba(255,255,255,0.78)" fontSize="lg" maxW="56ch">
              Far Better, a 7-year-old pacing gelding given a prognosis of
              "won't race again."
            </Text>
          </header>

          <div className="bioply-patient-grid">
            <MotionBox
              className="bioply-patient-visual"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-24px" }}
              transition={{ duration: 0.5 }}
              aria-hidden="true"
            >
              <div className="bioply-image-slot bioply-patient-image">
                <img src={patientImageUrl} alt="" loading="lazy" />
              </div>
            </MotionBox>

            <MotionBox
              className="bioply-patient-card"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-24px" }}
              transition={{ duration: 0.5, delay: 0.05 }}
            >
              <div className="bioply-patient-col bioply-patient-col-before">
                <div className="bioply-patient-col-eyebrow">Before BIO-PLY™</div>
                <h3 className="bioply-patient-col-title">
                  Standard of care fails
                </h3>
                <ul className="bioply-patient-list">
                  <li>Severe septic joint</li>
                  <li>Multiple surgeries</li>
                  <li>Not responding to antibiotics</li>
                  <li>Two years out of racing</li>
                  <li>Prognosis: "won't race again"</li>
                </ul>
              </div>
              <div className="bioply-patient-divider" aria-hidden="true">
                <span className="bioply-patient-arrow">
                  <Icon name="arrow_forward" />
                </span>
              </div>
              <div className="bioply-patient-col bioply-patient-col-after">
                <div className="bioply-patient-col-eyebrow">After BIO-PLY™</div>
                <h3 className="bioply-patient-col-title">Back to racing</h3>
                <ul className="bioply-patient-list">
                  <li>Rapid reduction in swelling</li>
                  <li>Restored joint flexibility</li>
                  <li>Returned to racing in month 4</li>
                  <li>Back in the winner's circle in month 5</li>
                </ul>
              </div>
            </MotionBox>
          </div>
        </section>

        {/* Veterinarian voices */}
        <section
          id="voices"
          className="landing-section bioply-quotes"
          aria-labelledby="quotes-heading"
        >
          <header className="bioply-quotes-header">
            <p className="bioply-overline">Veterinarian voices</p>
            <Heading
              as="h2"
              id="quotes-heading"
              className="bioply-display"
              color="gray.900"
              mb={3}
            >
              Heard from the field.
            </Heading>
          </header>
          <div
            className="bioply-carousel"
            role="group"
            aria-roledescription="carousel"
            aria-label="Veterinarian testimonials"
            onMouseEnter={() => setQuotesPaused(true)}
            onMouseLeave={() => setQuotesPaused(false)}
            onFocusCapture={handleQuoteFocusCapture}
            onBlurCapture={() => setQuotesPaused(false)}
          >
            {/* Announcing every auto-rotation would talk over the visitor, so the
                region only goes live once they take control (hover or focus). */}
            <div
              className="bioply-carousel-viewport"
              aria-live={quotesPaused ? "polite" : "off"}
            >
              <AnimatePresence mode="wait" initial={false}>
                <MotionBox
                  as="figure"
                  key={quoteIndex}
                  className="bioply-quote-card"
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -14 }}
                  transition={{ duration: 0.32 }}
                >
                  <div className="bioply-quote-mark" aria-hidden="true">
                    &ldquo;
                  </div>
                  <blockquote className="bioply-quote-card-body">
                    {activeQuote.quote}
                  </blockquote>
                  <figcaption className="bioply-quote-card-attribution">
                    <div
                      className="bioply-quote-avatar bioply-quote-avatar--monogram"
                      aria-hidden="true"
                    >
                      {activeQuote.monogram}
                    </div>
                    <div className="bioply-quote-meta">
                      <div className="bioply-quote-name">{activeQuote.name}</div>
                      <div className="bioply-quote-affiliation">
                        {activeQuote.affiliation}
                      </div>
                    </div>
                  </figcaption>
                </MotionBox>
              </AnimatePresence>
            </div>

            {TESTIMONIALS.length > 1 && (
              <div className="bioply-carousel-controls">
                <button
                  type="button"
                  className="bioply-carousel-arrow"
                  onClick={() => goToQuote(quoteIndex - 1)}
                  aria-label="Previous testimonial"
                >
                  <Icon name="arrow_back" />
                </button>
                <div className="bioply-carousel-dots">
                  {TESTIMONIALS.map((t, i) => (
                    <button
                      type="button"
                      key={t.name}
                      className={`bioply-carousel-dot${i === quoteIndex ? " is-active" : ""}`}
                      onClick={() => goToQuote(i)}
                      aria-label={`Testimonial ${i + 1} of ${TESTIMONIALS.length}`}
                      aria-current={i === quoteIndex}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  className="bioply-carousel-arrow"
                  onClick={() => goToQuote(quoteIndex + 1)}
                  aria-label="Next testimonial"
                >
                  <Icon name="arrow_forward" />
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Development status — synced with the Products pipeline */}
        <section
          id="status"
          className="landing-section bioply-status"
          aria-labelledby="status-heading"
        >
          <div className="bioply-trial">
            <div className="bioply-trial-head">
              <p className="bioply-overline">Development status</p>
              <h2 id="status-heading" className="bioply-trial-title">
                Where BIO-PLY™ is today
              </h2>
            </div>
            <div className="bioply-progress">
              <div className="bioply-progress-track">
                <div className="bioply-progress-rail" aria-hidden="true">
                  <div
                    className="bioply-progress-rail-fill"
                    style={{
                      width: `${((BIO_PLY_LEAD.stage - 1) / (STAGE_LABELS.length - 1)) * 100}%`,
                    }}
                  />
                </div>
                <ol className="bioply-progress-steps">
                  {STAGE_LABELS.map((label, i) => {
                    // `stage` is 1-indexed; `i` is 0-indexed.
                    const position = i + 1;
                    const state =
                      position < BIO_PLY_LEAD.stage
                        ? "done"
                        : position === BIO_PLY_LEAD.stage
                        ? "current"
                        : "upcoming";
                    return (
                      <li key={label} className={`bioply-progress-step is-${state}`}>
                        <span className="bioply-progress-dot" aria-hidden="true" />
                        <span className="bioply-progress-label">
                          {position === BIO_PLY_LEAD.stage
                            ? stageStatusLabel(BIO_PLY_LEAD)
                            : label}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              </div>
              <p className="bioply-progress-caption">
                <Icon name="schedule" aria-hidden="true" />
                {BIO_PLY_LEAD.indication} &middot; advancing through the FDA
                approval process
              </p>
              <p className="bioply-progress-footnote">{FDA_PATHWAY_FOOTNOTE}</p>
              <Link to="/products" className="bioply-progress-link">
                See the full product pipeline
                <Icon name="arrow_forward" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        {/* Order strip — single focused CTA; the contact form below handles questions */}
        <section className="bioply-order-cta" aria-labelledby="order-cta-heading">
          <div className="bioply-order-cta-inner">
            <div className="bioply-order-cta-text">
              <p className="bioply-overline bioply-overline--on-dark">Available now</p>
              <h2 id="order-cta-heading" className="bioply-order-cta-title">
                Ready to put BIO-PLY™ to work?
              </h2>
              <p className="bioply-order-cta-sub">
                Request access below — it's the official channel for veterinary
                practices to request BIO-PLY™.
              </p>
            </div>
            <Button
              as={Link}
              to="/order"
              size="lg"
              flexShrink={0}
              bg="white"
              color="var(--color-primary-maroon)"
              fontWeight={700}
              borderRadius="var(--radius-full)"
              px={8}
              _hover={{ bg: "whiteAlpha.900", transform: "translateY(-1px)" }}
              _active={{ transform: "translateY(0)" }}
              rightIcon={<Icon name="arrow_forward" />}
            >
              Request BIO-PLY™
            </Button>
          </div>
        </section>

        {/* Contact */}
        <section
          id="contact"
          className="landing-contact bioply-contact"
          aria-labelledby="contact-heading"
        >
          <div
            className="landing-contact-visual"
            style={{ backgroundImage: `url(${contactImageUrl})` }}
            aria-hidden="true"
          />
          <div className="landing-contact-inner">
            <div className="landing-contact-form-wrap bioply-contact-form-wrap">
              <header className="bioply-contact-header">
                <p className="bioply-overline bioply-overline--on-dark">
                  Contact us
                </p>
                <Heading
                  as="h2"
                  id="contact-heading"
                  size="xl"
                  color="white"
                  mb={4}
                >
                  Get in touch
                </Heading>
              </header>
              <div className="landing-contact-form-card bioply-contact-form-card">
                <div
                  className="landing-contact-form-accent"
                  aria-hidden="true"
                />
                {contactSubmitted ? (
                  <VStack align="stretch" spacing={3} role="status" py={2}>
                    <Box
                      aria-hidden="true"
                      alignSelf="flex-start"
                      display="inline-flex"
                      alignItems="center"
                      justifyContent="center"
                      boxSize="44px"
                      borderRadius="full"
                      bg="var(--color-primary-maroon)"
                      color="white"
                      fontSize="24px"
                    >
                      <Icon name="check" />
                    </Box>
                    <Text fontSize="lg" fontWeight={700} color="gray.800">
                      Thanks — we'll be in touch.
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      We've got your email and a member of our team will get back
                      to you shortly.
                    </Text>
                  </VStack>
                ) : (
                  <form
                    onSubmit={handleContactSubmit}
                    className="bioply-contact-form"
                  >
                    <VStack align="stretch" spacing={4}>
                      <Box>
                        <Text fontSize="sm" fontWeight={600} color="gray.700">
                          Request further information
                        </Text>
                        <Text fontSize="sm" color="gray.500" mt={1}>
                          Enter your email and we'll get back to you.
                        </Text>
                      </Box>
                      <Input
                        placeholder="Your email"
                        aria-label="Your email address"
                        autoComplete="email"
                        type="email"
                        bg="var(--color-gray-50)"
                        border="1px solid var(--color-gray-200)"
                        color="gray.800"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                        _placeholder={{ color: "gray.500" }}
                        _focus={{
                          borderColor: "var(--color-primary-maroon)",
                          boxShadow: "0 0 0 1px var(--color-primary-maroon)",
                        }}
                        borderRadius="var(--radius-md)"
                        size="lg"
                      />
                      <Turnstile
                        ref={turnstileRef}
                        onVerify={setCaptchaToken}
                        onExpire={() => setCaptchaToken("")}
                        onError={() => setCaptchaToken("")}
                      />
                      <Button
                        type="submit"
                        size="lg"
                        bg="transparent"
                        color="white"
                        background="linear-gradient(135deg, var(--color-cta-orange) 0%, var(--color-primary-maroon) 100%)"
                        _hover={{
                          background:
                            "linear-gradient(135deg, var(--color-cta-orange-hover) 0%, var(--color-primary-dark) 100%)",
                          boxShadow: "0 4px 14px rgba(139, 21, 56, 0.35)",
                        }}
                        borderRadius="var(--radius-md)"
                        fontWeight={600}
                        isLoading={contactSubmitting}
                        isDisabled={!captchaToken || !contactEmail.trim()}
                      >
                        Request information
                      </Button>
                      {contactError && (
                        <Text role="alert" fontSize="sm" color="#B42318">
                          {contactError}
                        </Text>
                      )}
                      <Text
                        fontSize="xs"
                        color="gray.500"
                        className="bioply-contact-form-note"
                        textAlign={{ base: 'center', md: 'left' }}
                      >
                        We'll respond within 24 hours.
                      </Text>
                    </VStack>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
      </main>

      <LandingFooter
          columns={[
            {
              heading: "Company",
              links: [
                { label: "Home", href: "/" },
                { label: "What is BIO-PLY™", href: "#what-is" },
                { label: "Contact", href: "#contact" },
              ],
            },
            {
              heading: "Resources",
              links: [
                { label: "Clinical results", href: "#proof" },
                { label: "Development status", href: "#status" },
              ],
            },
            {
              heading: "Support",
              links: [
                { label: "Login", href: "/login" },
                { label: "Request access", href: "#request-access" },
              ],
            },
          ]}
        />
    </div>
  );
}
